import { TelephonyCallStage, TelephonyDirection, TelephonySubjectType } from './telephony.events';

/**
 * Статус карточки звонка в очереди
 */
export type QueuedCallStatus = 'active' | 'pending_result' | 'completed';

/**
 * Приоритет/срочность карточки
 */
export type QueuedCallPriority = 'normal' | 'urgent' | 'overdue';

/**
 * Карточка звонка в очереди
 */
export interface QueuedCall {
  // Серверные поля (добавлены для синхронизации)
  id?: number;                      // PK из БД
  odUserId?: number;                // Владелец карточки (оператор)
  odUserFullName?: string;          // ФИО оператора (для супервайзора)
  serverCreatedAt?: string;         // ISO timestamp из БД
  serverUpdatedAt?: string;         // ISO timestamp из БД

  // Идентификация
  callId: string;
  phone: string;
  direction: TelephonyDirection;

  // Subject (договор/лид/тикет)
  subjectType?: TelephonySubjectType;
  subjectId?: string | number;
  stageCode?: string; // Код этапа договора для загрузки списка действий

  // Клиент
  customerId?: number;
  customerFullName?: string;
  customerIin?: string;

  // Состояние телефонии
  telephonyEvent?: TelephonyCallStage;
  recordingUrl?: string;
  recordingReady?: boolean;

  // Форма результата
  resultCode?: string;
  comment?: string;

  // Метаданные
  createdAt: number;
  lastActivityAt: number;

  // UI состояние
  status: QueuedCallStatus;
  priority: QueuedCallPriority;
  seen: boolean;
}

/**
 * Payload для добавления звонка в очередь
 */
export interface AddToQueuePayload {
  callId: string;
  phone: string;
  direction: TelephonyDirection;
  subjectType?: TelephonySubjectType;
  subjectId?: string | number;
  stageCode?: string;
  customerId?: number;
  customerFullName?: string;
  customerIin?: string;
  telephonyEvent?: TelephonyCallStage;
}

/**
 * Payload для обновления звонка в очереди
 */
export interface UpdateQueuedCallPayload {
  status?: QueuedCallStatus;
  telephonyEvent?: TelephonyCallStage;
  recordingUrl?: string;
  recordingReady?: boolean;
  resultCode?: string;
  comment?: string;
  seen?: boolean;
  customerId?: number;
  customerFullName?: string;
}

/**
 * Конфигурация таймеров приоритетов (в миллисекундах)
 */
export interface CallQueueTimerConfig {
  urgentThreshold: number; // 5 минут по умолчанию
  overdueThreshold: number; // 15 минут по умолчанию
}

/**
 * Настройки панели очереди
 */
export interface CallQueueSettings {
  notificationsEnabled: boolean;
  panelWidth: number;
}

/**
 * Состояние контекста очереди
 */
export interface CallQueueContextValue {
  // Данные
  queue: QueuedCall[];
  selectedCallId: string | null;

  // Состояние панели
  isExpanded: boolean;
  isCardMode: boolean;

  // Настройки
  settings: CallQueueSettings;

  // Состояние соединения
  isOnline: boolean;
  isSyncing: boolean;

  // Действия
  addToQueue: (payload: AddToQueuePayload) => void;
  updateCall: (callId: string, payload: UpdateQueuedCallPayload) => void;
  removeFromQueue: (callId: string) => void;
  submitResult: (callId: string, resultCode: string, comment?: string) => Promise<void>;

  // UI действия
  selectCall: (callId: string | null) => void;
  togglePanel: () => void;
  expandPanel: () => void;
  collapsePanel: () => void;
  enterCardMode: (callId: string) => void;
  exitCardMode: () => void;
  markAsSeen: (callId: string) => void;
  reorderQueue: (fromIndex: number, toIndex: number) => void;

  // Настройки
  updateSettings: (settings: Partial<CallQueueSettings>) => void;

  // Таймеры
  timerConfig: CallQueueTimerConfig;

  // Синхронизация (для внешнего использования)
  requestSync: () => void;
}

/**
 * Ключ localStorage для сохранения состояния
 */
export const CALL_QUEUE_STORAGE_KEY = 'call_queue_state';
export const CALL_QUEUE_SETTINGS_KEY = 'call_queue_settings';

/**
 * Максимальный возраст карточки (8 часов в мс)
 */
export const MAX_CALL_AGE_MS = 8 * 60 * 60 * 1000;

/**
 * Дефолтные пороги приоритетов
 */
export const DEFAULT_URGENT_THRESHOLD_MS = 5 * 60 * 1000; // 5 минут
export const DEFAULT_OVERDUE_THRESHOLD_MS = 15 * 60 * 1000; // 15 минут

/**
 * Ширина панели по умолчанию
 */
export const DEFAULT_PANEL_WIDTH = 360;
